import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { createIdentifierFromQuery } from '@confluence/graphql/entry-points/createIdentifierFromQuery.ts';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { getOrCreatePersonalizationQueryOwner } from '@confluence/content-unified-query/entry-points/personalizationQueryOwner.ts';
import {
	ViewPagePersonalizationQuery,
	type PersonalizationQuery,
} from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationQuery.ts';
import { getRequest } from 'relay-runtime';

declare global {
	interface Window {
		__PERSONALIZATION_QUERY_VARIABLES__?: PersonalizationQuery['variables'][];
	}
}

type RelayStreamEvent = {
	type: string;
	operation: string;
	variables: PersonalizationQuery['variables'];
};

/** Rebuild client-owned references; never serialize a server reference or refetch during hydration. */
export function initializePersonalizationQueryHydration(): void {
	// The eligible route/reader records exposure. Bootstrap also runs on unrelated SSR routes.
	if (!UNSAFE_expValEqualsNoExposure('confluence_exp_personalization_contract', 'isEnabled', true))
		return;
	const environment = getRelayEnvironment();
	const owner = getOrCreatePersonalizationQueryOwner(environment);
	for (const variables of window.__PERSONALIZATION_QUERY_VARIABLES__ ?? []) {
		owner.preload(variables, undefined, true);
	}
	if (!window.__SSR_REACT_STREAMING__) return;
	const { text, id } = getRequest(ViewPagePersonalizationQuery).params;
	const operation = text ? createIdentifierFromQuery(text) : `id:${id}`;
	const stream = getClientStream<RelayStreamEvent>('relay');
	const onResult = (event: RelayStreamEvent) => {
		if (event.type !== 'result' || event.operation !== operation) return;
		// The network listener has cached the response. Replaying through the existing environment
		// normalizes streamed records; no response cache or transport is introduced here.
		owner.preload(event.variables);
	};
	stream.addListener(onResult);
	owner.setHydrationCleanup(() => stream.removeListener(onResult));
}
